/**
 * Generate a custom share image with emoji grid for social media
 */

export interface ShareImageOptions {
  puzzleNumber: number;
  won: boolean;
  attempts: number;
  results: Array<Array<"correct" | "present" | "absent">>;
  inProgress?: boolean;
}

/**
 * Generate a canvas-based share image with emoji grid
 * Returns a data URL for the generated image
 */
export async function generateShareImage(options: ShareImageOptions): Promise<string> {
  const { puzzleNumber, won, attempts, results } = options;
  
  // Create canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context not available');
  
  // Set dimensions (1200x630 for OpenGraph)
  canvas.width = 1200;
  canvas.height = 630;
  
  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#1a1a2e');
  gradient.addColorStop(1, '#0f0f1e');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Draw Wormble branding
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 72px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Wormble', canvas.width / 2, 100);
  
  // Draw puzzle info
  ctx.font = '36px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = '#9ca3af';
  ctx.fillText(`Puzzle #${puzzleNumber}`, canvas.width / 2, 160);
  
  // Draw result text
  ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
  if (options.inProgress) {
    ctx.fillStyle = '#f59e0b'; // amber for in-progress
    ctx.fillText(`${attempts}/5 in progress...`, canvas.width / 2, 230);
  } else {
    ctx.fillStyle = won ? '#10b981' : '#ef4444';
    const resultText = won ? `Solved in ${attempts} ${attempts === 1 ? 'guess' : 'guesses'}!` : `Can you solve it?`;
    ctx.fillText(resultText, canvas.width / 2, 230);
  }
  
  // Draw emoji grid
  const tileSize = 80;
  const gap = 12;
  const gridWidth = 5 * tileSize + 4 * gap;
  const gridHeight = results.length * tileSize + (results.length - 1) * gap;
  const startX = (canvas.width - gridWidth) / 2;
  const startY = 280;
  
  results.forEach((row, rowIndex) => {
    row.forEach((status, colIndex) => {
      const x = startX + colIndex * (tileSize + gap);
      const y = startY + rowIndex * (tileSize + gap);
      
      // Draw tile background
      ctx.fillStyle = status === 'correct' ? '#10b981' : 
                     status === 'present' ? '#f59e0b' : '#374151';
      ctx.fillRect(x, y, tileSize, tileSize);
      
      // Draw tile border
      ctx.strokeStyle = '#1f2937';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, tileSize, tileSize);
    });
  });
  
  // Draw CTA
  ctx.font = '32px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.fillText('wormble.com/play', canvas.width / 2, startY + gridHeight + 70);
  
  // Convert to data URL
  return canvas.toDataURL('image/png');
}

/**
 * Download the share image
 */
export function downloadShareImage(dataUrl: string, filename: string = 'wormble-share.png') {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Share the generated image using Web Share API (if available)
 */
export async function shareGeneratedImage(dataUrl: string, options: ShareImageOptions): Promise<boolean> {
  try {
    // Convert data URL to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], 'wormble-share.png', { type: 'image/png' });
    
    // Check if Web Share API with files is supported
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        title: `Wormble — Puzzle #${options.puzzleNumber}`,
        text: options.won 
          ? `I solved Wormble #${options.puzzleNumber} in ${options.attempts} guesses!`
          : `Can you solve Wormble #${options.puzzleNumber}?`,
        files: [file],
      });
      return true;
    }
    
    return false;
  } catch (err) {
    console.error('Failed to share image:', err);
    return false;
  }
}
